import React, { useState } from "react";
import { Loader2, Check, X, ArrowRight, Download, Heart } from "lucide-react";

interface ButtonProps {
  children: React.ReactNode;
  variant?:
    | "primary"
    | "secondary"
    | "success"
    | "danger"
    | "warning"
    | "ghost"
    | "outline";
  size?: "sm" | "md" | "lg" | "xl";
  loading?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  onClick?: () => void;
  fullWidth?: boolean;
  rounded?: boolean;
  pulse?: boolean;
  glow?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = "primary",
  size = "sm",
  loading = false,
  disabled = false,
  icon,
  iconPosition = "left",
  onClick,
  fullWidth = false,
  rounded = false,
  pulse = false,
  glow = false,
}) => {
  const baseClasses =
    "inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 transform hover:scale-105 active:scale-95";
  const variantClasses = {
    primary:
      "bg-[#4199CA] text-white",
    secondary:
      "bg-[#008D4C] text-white",
    success:
      "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg hover:shadow-xl focus:ring-green-500",
    danger:
      "bg-[#D73925] text-white",
    warning:
      "bg-yellow-300 text-white",
    ghost:
      "bg-transparent hover:bg-gray-100 text-gray-700 hover:text-gray-900 focus:ring-gray-500",
    outline:
      "border-2 border-gray-300 hover:border-gray-400 bg-transparent hover:bg-gray-50 text-gray-700 hover:text-gray-900 focus:ring-gray-500",
  };

  const sizeClasses = {
    xs: "px-2 py-1 text-xs gap-1", // Extra Small
    sm: "px-3 py-1.5 text-sm gap-1.5", // Small
    md: "px-4 py-2 text-base gap-2", // Medium (default)
    lg: "px-6 py-3 text-lg gap-2.5", // Large
    xl: "px-8 py-4 text-xl gap-3", // Extra Large
  };

  const roundedClasses = rounded ? "rounded-full" : "rounded-lg";
  const widthClasses = fullWidth ? "w-full" : "";
  const pulseClasses = pulse ? "animate-pulse" : "";
  const glowClasses = glow ? "shadow-2xl shadow-blue-500/25" : "";
  const disabledClasses =
    disabled || loading
      ? "opacity-50 cursor-not-allowed transform-none hover:scale-100"
      : "";

  const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${roundedClasses} ${widthClasses} ${pulseClasses} ${glowClasses} ${disabledClasses}`;

  return (
    <button
      className={classes}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {loading && (
        <Loader2
          className={`animate-spin ${
            size === "sm"
              ? "w-4 h-4"
              : size === "lg"
              ? "w-6 h-6"
              : size === "xl"
              ? "w-7 h-7"
              : "w-5 h-5"
          }`}
        />
      )}
      {!loading && icon && iconPosition === "left" && (
        <span
          className={`${
            size === "sm"
              ? "w-4 h-4"
              : size === "lg"
              ? "w-6 h-6"
              : size === "xl"
              ? "w-7 h-7"
              : "w-5 h-5"
          }`}
        >
          {icon}
        </span>
      )}
      <span>{children}</span>
      {!loading && icon && iconPosition === "right" && (
        <span
          className={`${
            size === "sm"
              ? "w-4 h-4"
              : size === "lg"
              ? "w-6 h-6"
              : size === "xl"
              ? "w-7 h-7"
              : "w-5 h-5"
          }`}
        >
          {icon}
        </span>
      )}
    </button>
  );
};

export default Button;
